import { AgTechBadge } from '@agtech/web/components/Badges/AgTechBadge';
import AgTechTitleDetailHeader from '@agtech/web/components/Headers/AgTechTitleDetailHeader';
import AgTechListPopUp from '@agtech/web/components/Portals/Popups/AgTechListPopUp';
import { AgTechMoneyFormatter } from '@agtech/web/utilities/AgTechUtilities';
import { SaleLot } from '@sales/data/lots/LotData';
import { SaleLotCategory } from '@sales/data/lots/categories/LotCategoryData';
import _ from 'lodash';
import React from 'react'
import LotDialog from '../../../lots/dialogs/LotDialog';
import { useLotDataGetSaleLotDataQuery } from '@sales/data/lots/LotDataQueries';
import { LotDataGetInvoicableLotsForBidderQuery } from '@sales/data/lots/purchases/LotPurchaseDataQueries';
import { useAgTechQuery, useAgTechQueryResultCombination } from '@agtech/core/data/queries/AgTechDataQueries';
import AgTechQueryLoadingSurface from '@agtech/core/data/queries/AgTechQueryLoadingSurface';
import AgTechHeaderDetail from '@agtech/web/components/Headers/AgTechHeaderDetail';

export type BidderLotsListPopUpProps = {
    bidderId: number
}

type BidderLotsListPopUpPurchase = {
    id: number,
    lot: SaleLot,
    lotCategory?: SaleLotCategory,
    purchaseAmount: number
}

const BidderLotsListPopUp = (props: BidderLotsListPopUpProps) => {
    const lotListQuery = useBidderLotsListQuery(props);

    return (
        <div style={{ height: 240, width: 320 }}>
            <AgTechQueryLoadingSurface query={lotListQuery}>
                {(lotsPurchasedByBidder: BidderLotsListPopUpPurchase[]) => (
                    <AgTechListPopUp
                        header={{
                            title: `Lots Purchased (${lotsPurchasedByBidder.length})`
                        }}
                        maxItemsToShow={4}
                        list={{
                            items: lotsPurchasedByBidder,
                            itemRow: lotPurchase => {
                                return (
                                    <AgTechHeaderDetail
                                        header={'Lot ' + lotPurchase.lot.lotNumber}
                                        detail={`${lotPurchase.lot.saleOrder}${lotPurchase.lotCategory ? ' - ' + lotPurchase.lotCategory.name : ''}`}
                                        settings={{
                                            classes: 'stretched p-3 centered bottom-bordered table-bordered',
                                            header: {
                                                onClick: e => LotDialog.Open(e, { lotId: lotPurchase.lot.id })
                                            },
                                            badge: {
                                                icon: 'fas fa-bars',
                                                classes: 'mr-3 text-xs',
                                                badgeSize: 24
                                            },
                                            content: {
                                                body: () => (
                                                    <AgTechBadge
                                                        text={lotPurchase.purchaseAmount
                                                            ? AgTechMoneyFormatter.format(lotPurchase.purchaseAmount)
                                                            : 'N/A'}
                                                        classes='color-green w-[80px]'
                                                    />
                                                )
                                            }
                                        }}
                                    />
                                )
                            },
                            itemHeight: 70,
                            noRecordsConfig: {
                                message: 'No lots purchased'
                            }
                        }}
                        footer={() => (
                            <div className='wide column-based-vertically-centered container'>
                                <AgTechTitleDetailHeader
                                    title='Total'
                                    titleFontSize='font-size-small'
                                    detail={AgTechMoneyFormatter.format(_.sumBy(lotsPurchasedByBidder, p => p.purchaseAmount))}
                                    contentClasses='centered'
                                    // content={() => (
                                    //     <InvoiceCreationDialog.Button
                                    //         bidderId={props.bidderId}
                                    //         icon='plus'
                                    //         text='Invoice Bidder'
                                    //         classes='highlight'
                                    //         height={32}
                                    //     />
                                    // )}
                                    classes='py-half'
                                />
                            </div>
                        )}
                        width={320}
                    />
                )}
            </AgTechQueryLoadingSurface>
        </div>
    )
}

const useBidderLotsListQuery = (props: { bidderId: number }) => {
    const lotDataQuery = useLotDataGetSaleLotDataQuery();

    const getInvoiceLotsForBidderQuery = useAgTechQuery({
        query: LotDataGetInvoicableLotsForBidderQuery,
        queryContext: { bidderId: props.bidderId }
    });

    return useAgTechQueryResultCombination({
        firstQuery: lotDataQuery,
        secondQuery: getInvoiceLotsForBidderQuery,
        projection: (lotData, bidderLots) => {
            return bidderLots.map(purchase => {
                let lot = lotData.lots[purchase.lotId];

                return {
                    id: lot.id,
                    lot: lot,
                    lotCategory: lot.lotCategoryId ? lotData.lotCategories[lot.lotCategoryId] : undefined,
                    purchaseAmount: (lot.purchasePrice ?? 0) * purchase.percentage 
                }
            });
        }
    })
}

export default BidderLotsListPopUp